import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/api/core/auth/auth.service';
import { CoreService } from 'src/app/api/core/core/core.service';
import { AnalyticsService } from '../../analytics/analytics.service';
import { CommsService } from '../comms.service';
import { VenueConfig } from '@hungryhungry/insights-schema';

@Injectable({
  providedIn: 'root',
})
export class BffService {
  private REQUEST_TIMEOUT: number = 20000;

  constructor(
    public comms: CommsService,
    public auth: AuthService,
    public core: CoreService,
    public analytics: AnalyticsService,
  ) {}

  /**
   * Generates HttpHeaders containing the the Authorization token of the BFF server
   * @returns HttpHeaders with Authorization
   */
  getHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${this.core.Active_server.bff.token}`,
    });
  }

  /**
   * Retrieves the cached dockets from BFF docket cache.
   *
   * Only dockets within the bump screen exclude time is included
   *
   * @returns Response payload
   */
  public getCachedDockets() {
    return new Promise(cacheResolver => {
      this.comms.post(
        `${this.core.Active_server.bff.url}docket/cachedDockets`,
        {
          locations: this.auth.Active_bump_screen.location_details.map(({ id }) => id),
          exclude_dockets_after: +this.auth.Active_bump_screen.config.exclude_orders_after || 720,
        },
        {
          headers: this.getHeaders(),
        },
        success => {
          this.analytics.trackEvent('Docket Cache', 'Success', this.auth.Tracking_key);
          cacheResolver(success);
        },
        fail => {
          this.analytics.trackEvent('Docket Cache', 'Failed', this.auth.Tracking_key);
          cacheResolver(fail);
        },
        this.REQUEST_TIMEOUT,
      );
    });
  }

  /**
   * Updates the status of the order. It could be the ETA, Printed, Bump or
   * SMS received status.
   *
   * @returns Response payload
   */
  public updateOrder(requestBody: any) {
    return new Promise((updateResolver, updateReject) => {
      this.comms.post(
        `${this.core.Active_server.bff.url}order/update`,
        requestBody,
        {
          headers: this.getHeaders(),
        },
        payload => {
          updateResolver(payload);
        },
        fail => {
          updateReject(fail);
        },
        this.REQUEST_TIMEOUT,
      );
    });
  }

  /**
   * Sends a HTTP request to retrieve the bump screen docket layout config
   * @returns Response payload
   */
  public getBFFDocketConfiguration() {
    return new Promise((configResolver, configReject) => {
      this.comms.get(
        `${this.core.Active_server.bff.url}docket/getDocketConfig/${this.auth.Active_bump_screen.id}`,
        {
          headers: this.getHeaders(),
        },
        payload => {
          configResolver(payload);
        },
        fail => {
          configReject(fail);
        },
        this.REQUEST_TIMEOUT,
      );
    });
  }

  /**
   * Sends a HTTP request to update the bump screen docket layout config
   * @param requestData The request data containing the bump screen id and new config
   * @returns Response payload
   */
  public updateBFFDocketConfig(requestData: any) {
    return new Promise((updateResolver, updateReject) => {
      this.comms.put(
        `${this.core.Active_server.bff.url}docket/updateDocketConfig`,
        requestData,
        {
          headers: this.getHeaders(),
        },
        payload => {
          updateResolver(payload);
        },
        fail => {
          updateReject(fail);
        },
        this.REQUEST_TIMEOUT,
      );
    });
  }

  /**
   * Sends a HTTP request to update the vip customers
   * @param accountId The venues account id
   */
  public refreshVipInsights(accountId: string) {
    return new Promise(cacheResolver => {
      this.comms.post(
        `${this.core.Active_server.bff.url}insights/vips/${accountId}/refresh`,
        {},
        {
          headers: this.getHeaders(),
        },
        success => {
          this.analytics.trackEvent('VIP Cache refresh', 'Success', this.auth.Tracking_key);
          cacheResolver(success);
        },
        fail => {
          this.analytics.trackEvent('VIP Cache refresh', 'Failed', this.auth.Tracking_key);
          cacheResolver(fail);
        },
        this.REQUEST_TIMEOUT,
      );
    });
  }

  public getVipConfig(accountId: string): Promise<VenueConfig> {
    return new Promise((configResolver, configReject) => {
      this.comms.get(
        `${this.core.Active_server.bff.url}insights/config/${accountId}`,
        {
          headers: this.getHeaders(),
        },
        payload => {
          configResolver(payload);
        },
        fail => {
          configReject(fail);
        },
        this.REQUEST_TIMEOUT,
      );
    });
  }

  public setVipConfig(accountId: string, newConfig: VenueConfig) {
    return new Promise(cacheResolver => {
      this.comms.post(
        `${this.core.Active_server.bff.url}insights/config/${accountId}`,
        newConfig,
        {
          headers: this.getHeaders(),
        },
        success => {
          this.analytics.trackEvent('Saved VIP config successfully saved', 'Success', this.auth.Tracking_key);
          cacheResolver(success);
        },
        fail => {
          this.analytics.trackEvent('Failed to save VIP config', 'Failed', this.auth.Tracking_key);
          cacheResolver(fail);
        },
        this.REQUEST_TIMEOUT,
      );
    });
  }
}
